import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, errorText } from "@/lib/api";

export const TYPES = {
  pristilbud: "Pristilbud – tilpasning / reparasjon",
  bunad: "Bunad – tilpasning og justering",
  brudekjole: "Brudekjole eller festantrekk",
  kurs: "Sykurs / personlig veiledning",
  bok: "Håndbok «Reparer klærne dine selv»",
  gruppekurs: "Gruppekurs – 4 timer (interesse)",
  nettkurs: "Nettkurs – kommer snart (interesse)",
  pakke: "Sykurs + digital håndbok (interesse)",
  booking: "Ønske om time",
  kontakt: "Generelt spørsmål",
};

const INTEREST = ["gruppekurs", "nettkurs", "pakke"];

const empty = { name: "", email: "", phone: "", message: "" };

export const InquiryForm = ({ defaultType = "pristilbud" }) => {
  const [type, setType] = useState(defaultType);
  const [f, setF] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const addPhotos = async (e) => {
    const files = [...e.target.files].slice(0, 3 - photos.length);
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await http.post("/inquiries/photos", fd);
        setPhotos((p) => [...p, { path: data.storage_path, preview: URL.createObjectURL(file) }]);
      } catch (err) { toast.error(errorText(err)); }
    }
    setUploading(false);
    e.target.value = "";
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Du må samtykke til behandling av personopplysninger.");
    setBusy(true);
    try {
      await http.post("/inquiries", { ...f, type, consent, photos: photos.map((p) => p.path) });
      setSent(true);
      setF(empty);
      setPhotos([]);
      toast.success("Takk! Forespørselen er sendt til Liisa.");
    } catch (err) {
      toast.error(errorText(err));
    }
    setBusy(false);
  };

  if (sent)
    return (
      <div className="border border-[#C4A47C] p-10 bg-[#F0EBE1]" data-testid="inquiry-success">
        <p className="font-serif-display text-3xl">Takk for din henvendelse</p>
        <p className="mt-4 text-[#5C5A56] leading-relaxed">Liisa går gjennom forespørselen og svarer deg så snart som mulig. Merk: dette er en forespørsel, ikke en bekreftet bestilling.</p>
        <button onClick={() => setSent(false)} className="mt-6 text-sm underline underline-offset-4 text-[#8C6E48]" data-testid="inquiry-new-btn">Send en ny forespørsel</button>
      </div>
    );

  const field = "rounded-none bg-[#FDFBF7] border-[#E5E0D8] h-12 focus-visible:ring-1 focus-visible:ring-[#C4A47C]";
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="inquiry-form">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className={field} data-testid="inquiry-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`inquiry-type-${k}`}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="grid sm:grid-cols-2 gap-5">
        <Input required placeholder="Navn" value={f.name} onChange={set("name")} className={field} data-testid="inquiry-name-input" />
        <Input placeholder="Telefon (valgfritt)" value={f.phone} onChange={set("phone")} className={field} data-testid="inquiry-phone-input" />
      </div>
      <Input required type="email" placeholder="E-post" value={f.email} onChange={set("email")} className={field} data-testid="inquiry-email-input" />
      <Textarea required rows={5} placeholder="Beskriv plagget og hva du ønsker – f.eks. type plagg, ønsket endring og når du trenger det." value={f.message} onChange={set("message")} className="rounded-none bg-[#FDFBF7] border-[#E5E0D8] focus-visible:ring-1 focus-visible:ring-[#C4A47C]" data-testid="inquiry-message-input" />
      <div data-testid="inquiry-photos">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={addPhotos} data-testid="inquiry-photo-input" />
        <div className="flex flex-wrap gap-3 items-center">
          {photos.map((p, i) => (
            <div key={p.path} className="relative w-20 h-20">
              <img src={p.preview} alt="" className="w-20 h-20 object-cover" />
              <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-[#1A1A1A] text-white rounded-full p-0.5" data-testid={`inquiry-photo-remove-${i}`}><X className="w-3 h-3" /></button>
            </div>
          ))}
          {photos.length < 3 && (
            <button type="button" onClick={() => fileRef.current.click()} disabled={uploading} className="flex items-center gap-2 px-4 h-12 border border-dashed border-[#C4A47C] text-sm text-[#8C6E48] hover:bg-[#F0EBE1] transition-colors disabled:opacity-50" data-testid="inquiry-photo-btn">
              <ImagePlus className="w-4 h-4" strokeWidth={1.5} />{uploading ? "Laster opp…" : "Legg ved bilde av plagget (valgfritt, maks 3)"}
            </button>
          )}
        </div>
      </div>
      <label className="flex items-start gap-3 text-xs text-[#5C5A56] leading-relaxed cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 rounded-none" data-testid="inquiry-consent-checkbox" />
        <span>Jeg samtykker til at SømSignature lagrer opplysningene mine for å besvare henvendelsen. Les mer i <Link to="/personvern" className="underline">personvernerklæringen</Link>.</span>
      </label>
      <button type="submit" disabled={busy} className="w-full sm:w-auto px-10 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm tracking-wide hover:bg-[#8C6E48] transition-colors duration-300 disabled:opacity-50" data-testid="inquiry-submit-btn">
        {busy ? "Sender…" : INTEREST.includes(type) ? "Meld interesse" : "Send forespørsel"}
      </button>
      <p className="text-xs text-[#5C5A56]" data-testid="inquiry-note">{INTEREST.includes(type) ? "Interesseregistrering er gratis og uforpliktende – det er ikke et kjøp. Kursdato og endelig pris kunngjøres senere." : "En forespørsel er ikke en bekreftet bestilling. Liisa tar kontakt med pris og tidspunkt."}</p>
    </form>
  );
};
